import React, { useState, useCallback, useEffect } from "react";
import { StyleSheet, View, ScrollView, Pressable } from "react-native";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DatePickerModal } from "react-native-paper-dates";

import TextEditor from "../../TextEditor/TextEditor";
import {
  useCreateAnnouncementMutation,
  useGetCoursesBySchoolByQueryQuery,
  useGetDegreeBySchoolQuery,
  useUpdateAnnouncementMutation,
  AudienceType,
  ActivityState,
  type AnnouncementWithStatus,
} from "@axio/graphql/generated/graphql";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import NewCustomAxioActionModal from "../../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import SelectableList from "../../common/form/SelectableList/SelectableList";
import DatePickerButton from "../../general/DatePickerButton/DatePickerButton";
import RadioButton from "../../common/form/RadioButton/RadioButton";
import { useAuth } from "../../../hooks/useAuth";
import { RolePermissions } from "../../../constants/rolesAndPermissions";
import { Checked, UnChecked } from "../../svgs/common";
import { color } from "@rneui/base";

const modals = {
  addConfirmation: {
    title: "Success!",
    ariaLabel: "Announcement Created Successfully Modal",
    warningText: "The announcement has been created successfully.",
    actionButtonText: "OK",
  },
  editConfirmation: {
    title: "Success!",
    ariaLabel: "Announcement Updated Successfully Modal",
    warningText: "The announcement has been updated successfully.",
    actionButtonText: "OK",
  },
  addError: {
    title: "Error!",
    ariaLabel: "Announcement Creation Error Modal",
    warningText: "An error occurred while creating the announcement. Please try again.",
    actionButtonText: "CLOSE",
  },
  editError: {
    title: "Error!",
    ariaLabel: "Announcement Update Error Modal",
    warningText: "An error occurred while updating the announcement. Please try again.",
    actionButtonText: "CLOSE",
  },
};

const getTargetAudienceOptions = (styles: any) => [
  {
    value: AudienceType.School,
    label: "Schoolwide",
    description: "All students, teachers, and faculty",
    component: (
      <View style={styles.targetAudienceItem}>
        <CustomText text="Schoolwide" size="s" style={styles.targetTitle} />
        <CustomText text="All students, teachers, and faculty" size="xs" style={styles.targetDescription} />
      </View>
    ),
  },
  {
    value: AudienceType.Students,
    label: "All Students",
    description: "Every student in the school",
    component: (
      <View style={styles.targetAudienceItem}>
        <CustomText text="All Students" size="s" style={styles.targetTitle} />
        <CustomText text="Every student in the school" size="xs" style={styles.targetDescription} />
      </View>
    ),
  },
  {
    value: AudienceType.Faculty,
    label: "All Faculty",
    description: "All faculty members",
    component: (
      <View style={styles.targetAudienceItem}>
        <CustomText text="All Faculty" size="s" style={styles.targetTitle} />
        <CustomText text="All faculty members" size="xs" style={styles.targetDescription} />
      </View>
    ),
  },
  {
    value: AudienceType.SpecificGroups,
    label: "Specific Groups",
    description: "Target specific classes, degrees, or grade levels",
    component: (
      <View style={styles.targetAudienceItem}>
        <CustomText text="Specific Groups" size="s" style={styles.targetTitle} />
        <CustomText
          text="Target specific classes, degrees, or grade levels"
          size="xs"
          style={styles.targetDescription}
        />
      </View>
    ),
  },
];

const publishOptions = [
  { value: ActivityState.Published, label: "Publish Now" },
  // { value: ActivityState.Scheduled, label: "Schedule" },
  { value: ActivityState.Draft, label: "Save as Draft" },
];

const priorityOptions = [
  { value: "normal", label: "Normal" },
  { value: "high", label: "High Priority" },
  { value: "urgent", label: "Urgent" },
];

// Specific targeting options
const gradeLevelOptions = [
  { value: "freshman", label: "Freshman" },
  { value: "sophomore", label: "Sophomore" },
  { value: "junior", label: "Junior" },
  { value: "senior", label: "Senior" },
  { value: "graduate", label: "Graduate" },
];

const departmentOptions = [
  { value: "computer-science-dept", label: "Computer Science Department" },
  { value: "business-dept", label: "Business Department" },
  { value: "engineering-dept", label: "Engineering Department" },
  { value: "liberal-arts-dept", label: "Liberal Arts Department" },
  { value: "nursing-dept", label: "Nursing Department" },
  { value: "education-dept", label: "Education Department" },
  { value: "psychology-dept", label: "Psychology Department" },
  { value: "biology-dept", label: "Biology Department" },
  { value: "chemistry-dept", label: "Chemistry Department" },
  { value: "mathematics-dept", label: "Mathematics Department" },
  { value: "english-dept", label: "English Department" },
  { value: "history-dept", label: "History Department" },
  { value: "art-dept", label: "Art Department" },
  { value: "music-dept", label: "Music Department" },
  { value: "communications-dept", label: "Communications Department" },
  { value: "economics-dept", label: "Economics Department" },
  { value: "political-science-dept", label: "Political Science Department" },
  { value: "sociology-dept", label: "Sociology Department" },
];

export enum AnnouncementModalState {
  CLOSED = "CLOSED",
  ADD_CONFIRMATION = "ADD_CONFIRMATION",
  EDIT_CONFIRMATION = "EDIT_CONFIRMATION",
  ADD_ERROR = "ADD_ERROR",
  EDIT_ERROR = "EDIT_ERROR",
}

const announcementSchema = z.object({
  title: z.string().min(1, "Title is required").max(100, "Title must be 100 characters or less"),
  message: z.string().min(1, "Message is required").max(5000, "Message must be 5000 characters or less"),
  audienceType: z.string().min(1, "Target audience is required"),
  publishOption: z.nativeEnum(ActivityState),
  expirationDate: z.date().optional(),
  // Specific targeting fields
  degreePrograms: z.array(z.string()).optional(),
  specificCourses: z.array(z.string()).optional(),
});

type AnnouncementFormData = z.infer<typeof announcementSchema>;

interface AnnouncementModalProps {
  visible: boolean;
  onClose: () => void;
  schoolId?: string; // For school-wide announcements
  announcement?: AnnouncementWithStatus | null;
  onSuccess?: () => void; // Callback for successful operations
  disabledAudienceUpdate?: boolean;
  isFaculty?: boolean;
}

const AnnouncementModal = ({
  visible,
  onClose,
  schoolId,
  announcement,
  onSuccess,
  disabledAudienceUpdate = false,
  isFaculty = false,
}: AnnouncementModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: AnnouncementModalState.CLOSED,
  });
  const { userInfo } = useAuth();
  const isAdmin = userInfo.userPermissions.includes(RolePermissions.auditView);

  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);

  const [createAnnouncement] = useCreateAnnouncementMutation();
  const [updateAnnouncement] = useUpdateAnnouncementMutation();

  const isEditing = !!announcement?.id;

  const { data: coursesData, loading: coursesDataLoading } = useGetCoursesBySchoolByQueryQuery({
    variables: { schoolId: schoolId },
    skip: !schoolId,
  });
  const { data: degreesData, loading: degreesDataLoading } = useGetDegreeBySchoolQuery({
    variables: { schoolId: schoolId },
    skip: !schoolId,
  });

  const degrees = degreesData?.getDegreeBySchool?.data ?? [];
  const courses = coursesData?.getCoursesBySchoolByQuery?.data?.courses ?? [];

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
    setValue,
    watch,
  } = useForm<AnnouncementFormData>({
    resolver: zodResolver(announcementSchema),
    defaultValues: {
      title: "",
      message: "",
      audienceType: "",
      publishOption: ActivityState.Published,
      expirationDate: undefined,
      degreePrograms: [],
      specificCourses: [],
    },
  });

  // Reset form when modal opens/closes or announcement changes
  useEffect(() => {
    if (visible) {
      if (announcement) {
        // Edit mode - populate form with existing data
        reset({
          title: announcement.title || "",
          message: announcement.message || "",
          audienceType: announcement.audienceType, // Default since not stored in backend
          publishOption: announcement.activityState,
          expirationDate: announcement.expirationDate ? new Date(announcement.expirationDate) : undefined,
          degreePrograms: announcement.degrees?.map((degree) => degree.id),
          specificCourses: announcement.courses?.map((course) => course.id),
        });
      } else {
        // Create mode - reset to defaults
        reset({
          title: "",
          message: "",
          audienceType: AudienceType.School,
          publishOption: ActivityState.Published,
          expirationDate: undefined,
          degreePrograms: [],
          specificCourses: [],
        });
      }
    }
  }, [visible, announcement, reset]);

  const handleClose = () => {
    onClose();
    reset();
    setIsDatePickerVisible(false);
  };

  const handleDatePickerDismiss = useCallback(() => {
    setIsDatePickerVisible(false);
  }, []);

  const handleDatePickerConfirm = useCallback(
    (params: { date: Date }) => {
      setValue("expirationDate", params.date);
      setIsDatePickerVisible(false);
    },
    [setValue]
  );

  const handleDatePick = () => {
    setIsDatePickerVisible(true);
  };

  const watchAudienceType = watch("audienceType");

  const handleFormSubmit = React.useCallback(
    async (formData: AnnouncementFormData) => {
      try {
        setIsSubmitting(true);

        if (isEditing) {
          // Update existing announcement
          const result = await updateAnnouncement({
            variables: {
              id: announcement.id,
              input: {
                title: formData.title,
                message: formData.message,
                expirationDate: formData.expirationDate?.toISOString() || null,
                schoolId: schoolId, // Required for school-scoped announcements
                audienceType: formData.audienceType as AudienceType,
                courseIds: formData.specificCourses,
                degreeIds: formData.degreePrograms,
                activityState: formData.publishOption,
                // TODO: Add specific targeting fields when backend supports them
                // gradeLevels: formData.gradeLevels,
                // departments: formData.departments,
              },
            },
          });

          if (result.data?.updateAnnouncement?.success) {
            setModalState({ state: AnnouncementModalState.EDIT_CONFIRMATION });
            onSuccess?.();
          } else {
            throw new Error(result.data?.updateAnnouncement?.message || "Failed to update announcement");
          }
        } else {
          // Create new announcement
          const result = await createAnnouncement({
            variables: {
              input: {
                title: formData.title,
                message: formData.message,
                expirationDate: formData.expirationDate?.toISOString() || null,
                schoolId: schoolId, // Required for school-scoped announcements
                audienceType: isFaculty ? AudienceType.SpecificGroups : (formData.audienceType as AudienceType),
                degreeIds: formData.degreePrograms ?? [],
                courseIds: formData.specificCourses ?? [],
                activityState: formData.publishOption,
                // TODO: Add specific targeting fields when backend supports them
                // degreePrograms: formData.degreePrograms,
                // gradeLevels: formData.gradeLevels,
                // specificCourses: formData.specificCourses,
                // departments: formData.departments,
              },
            },
          });

          if (result.data?.createAnnouncement?.success) {
            setModalState({ state: AnnouncementModalState.ADD_CONFIRMATION });
            onSuccess?.();
          } else {
            throw new Error(result.data?.createAnnouncement?.message || "Failed to create announcement");
          }
        }

        setIsSubmitting(false);
      } catch (error) {
        console.error("Error submitting form:", error);
        setModalState({
          state: isEditing ? AnnouncementModalState.EDIT_ERROR : AnnouncementModalState.ADD_ERROR,
        });
        setIsSubmitting(false);
      }
    },
    [isEditing, announcement, createAnnouncement, updateAnnouncement, onSuccess]
  );

  const getModalTitle = () => {
    return isEditing ? "Edit Announcement" : isFaculty ? "Quick Announcement" : "New Admin Announcement";
  };

  const getSubmitButtonText = () => {
    if (isFaculty) {
      return watch("publishOption") === ActivityState.Draft ? "Save as Draft" : "Publish";
    }
    if (isSubmitting) {
      return isEditing ? "Updating..." : "Posting...";
    }
    return isEditing ? "Update Announcement" : "Post Announcement";
  };

  return (
    <>
      <CustomAdminModal
        visible={visible}
        onRequestClose={handleClose}
        title={getModalTitle()}
        ariaLabel={isEditing ? "Edit announcement modal" : "Create announcement modal"}
        style={{
          width: isMedium ? "80%" : "90%",
          maxHeight: "90%",
          maxWidth: isFaculty ? 640 : 1200,
          borderRadius: 16,
        }}>
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
          <View style={styles.container}>
            {/* Title */}
            <View style={styles.formGroup}>
              <View style={{ display: "flex", flexDirection: "row" }}>
                <CustomText
                  text="Title"
                  size="s"
                  weight={isFaculty ? "medium" : "ultraBold"}
                  style={styles.formLabel}
                />
                {isFaculty && (
                  <CustomText
                    text=" *"
                    size="s"
                    weight="medium"
                    style={{ color: "#EF4444" }}
                    useTranslationText={false}
                  />
                )}
              </View>
              <CustomSpacing size="s" type="vertical" />
              <Controller
                control={control}
                name="title"
                render={({ field: { onChange, value } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={onChange}
                    placeholder="Announcement title"
                    maxLength={100}
                    style={styles.input}
                    onToggleSecureEntry={undefined}
                  />
                )}
              />
              {errors?.title?.message && <CustomText size="xs" text={errors.title?.message} style={styles.formError} />}
            </View>

            {isFaculty && (
              <View style={styles.formGroup}>
                <View style={{ display: "flex", flexDirection: "row" }}>
                  <CustomText
                    text="Post to Course"
                    size="s"
                    weight="medium"
                    style={{ color: "#374151" }}
                    useTranslationText={false}
                  />
                  <CustomText
                    text=" *"
                    size="s"
                    weight="medium"
                    style={{ color: "#EF4444" }}
                    useTranslationText={false}
                  />
                </View>
                <Controller
                  control={control}
                  name="specificCourses"
                  render={({ field: { onChange, value } }) => (
                    <View style={styles.multiSelectContainer}>
                      <View style={styles.courseSelectionBox}>
                        <ScrollView style={styles.multiSelectCourseBox} nestedScrollEnabled tabIndex={0}>
                          {courses.map(
                            (option) =>
                              !option.isDraft && (
                                <Pressable
                                  key={option.id}
                                  accessibilityRole="checkbox"
                                  accessibilityState={{ checked: value?.includes(option.id) }}
                                  style={({ pressed, hovered }: any) => [
                                    styles.multiSelectOption,
                                    value?.includes(option.id) && styles.multiSelectOptionSelected,
                                    hovered && styles.multiSelectOptionHovered,
                                    pressed && styles.multiSelectOptionPressed,
                                  ]}
                                  onPress={() => {
                                    const newValue = value?.includes(option.id)
                                      ? value.filter((v: string) => v !== option.id)
                                      : [...(value || []), option.id];
                                    onChange(newValue);
                                  }}>
                                  <View
                                    style={{ display: "flex", flexDirection: "row", gap: 12, alignItems: "center" }}>
                                    <CustomButton
                                      styleType="transparent"
                                      role="checkbox"
                                      aria-checked={option.name}
                                      aria-label="checkbox button"
                                      accessibilityLabel="checkbox button"
                                      style={styles.checkboxButton}
                                      leftIcon={
                                        value?.includes(option.id) ? (
                                          <Checked
                                            accessibilityLabel="checkbox button checked"
                                            role="img"
                                            alt="checkbox button checked"
                                          />
                                        ) : (
                                          <UnChecked
                                            accessibilityLabel="checkbox button unchecked"
                                            role="img"
                                            alt="checkbox button unchecked"
                                          />
                                        )
                                      }
                                      onPress={() => {
                                        const newValue = value?.includes(option.id)
                                          ? value.filter((v: string) => v !== option.id)
                                          : [...(value || []), option.id];
                                        onChange(newValue);
                                      }}
                                    />
                                    <View
                                      style={{
                                        display: "flex",
                                        flex: 1,
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}>
                                      <View
                                        style={{
                                          display: "flex",
                                          flexDirection: "row",
                                          gap: 8,
                                          alignItems: "center",
                                        }}>
                                        <CustomText
                                          text={option.course_code}
                                          size="m"
                                          weight="medium"
                                          useTranslationText={false}
                                          style={{ color: "#101828" }}
                                        />
                                        <CustomText
                                          text={option.name}
                                          size="s"
                                          useTranslationText={false}
                                          style={{ color: "#344054" }}
                                        />
                                      </View>
                                      <CustomText
                                        text={option.enrolledStudents.length}
                                        size="xs"
                                        useTranslationText={false}
                                        style={{ color: "#667085" }}
                                      />
                                    </View>
                                  </View>
                                </Pressable>
                              )
                          )}
                        </ScrollView>
                      </View>
                    </View>
                  )}
                />
              </View>
            )}

            {/* Message */}
            <View style={styles.formGroup}>
              <View style={{ display: "flex", flexDirection: "row" }}>
                <CustomText
                  text="Message"
                  size="s"
                  weight={isFaculty ? "medium" : "ultraBold"}
                  style={styles.formLabel}
                />
                {isFaculty && (
                  <CustomText
                    text=" *"
                    size="s"
                    weight="medium"
                    style={{ color: "#EF4444" }}
                    useTranslationText={false}
                  />
                )}
              </View>
              <CustomSpacing size="s" type="vertical" />
              <Controller
                control={control}
                name="message"
                render={({ field: { onChange, value } }) => (
                  <TextEditor value={value} onChange={onChange} placeholder="Write your announcement..." />
                )}
              />
              <CustomText
                text="characters_remaining"
                interpolation={{ charactersRemaining: 5000 - (getValues("message")?.length || 0) }}
                size="xs"
                style={styles.helpText}
              />
              {errors?.message?.message && (
                <CustomText size="xs" text={errors.message?.message} style={styles.formError} />
              )}
            </View>

            {/* Target Audience */}
            {!disabledAudienceUpdate && !isFaculty && (
              <View style={[styles.formGroup, { marginBottom: 0 }]}>
                <CustomText text="Target Audience" size="s" weight="ultraBold" style={styles.formLabel} />
                <Controller
                  control={control}
                  name="audienceType"
                  render={({ field: { onChange, value } }) => (
                    <SelectableList
                      items={getTargetAudienceOptions(styles)}
                      onSelect={onChange}
                      selectedItem={value}
                      selectItemStyle={styles.selectItem}
                      selectedItemStyle={styles.selectedItem}
                      fullWidth={true}
                      ariaLabel="Target audience options"
                    />
                  )}
                />
                {errors?.audienceType?.message && (
                  <CustomText size="xs" text={errors.audienceType?.message} style={styles.formError} />
                )}
              </View>
            )}

            {/* Specific Targeting Section - Only show when "specific" is selected */}
            {!disabledAudienceUpdate && watchAudienceType === AudienceType.SpecificGroups && (
              <View style={styles.targetingSection}>
                <CustomText
                  text="Select Specific Targets"
                  size="s"
                  weight="ultraBold"
                  style={styles.targetingSectionTitle}
                />

                {/* By Degree Program */}
                <View>
                  <CustomText text={"By Degree Program"} size="xs" weight="bold" style={styles.targetingLabel} />
                  <Controller
                    control={control}
                    name="degreePrograms"
                    render={({ field: { onChange, value } }) => (
                      <View style={styles.multiSelectContainer}>
                        <View style={styles.multiSelectBox}>
                          <ScrollView style={styles.multiSelectScroll} nestedScrollEnabled tabIndex={0}>
                            {degrees.map((option) => (
                              <Pressable
                                key={option.id}
                                accessibilityRole="checkbox"
                                accessibilityState={{ checked: value?.includes(option.id) }}
                                style={({ pressed, hovered }: any) => [
                                  styles.multiSelectOption,
                                  value?.includes(option.id) && styles.multiSelectOptionSelected,
                                  hovered && styles.multiSelectOptionHovered,
                                  pressed && styles.multiSelectOptionPressed,
                                ]}
                                onPress={() => {
                                  const newValue = value?.includes(option.id)
                                    ? value.filter((v: string) => v !== option.id)
                                    : [...(value || []), option.id];
                                  onChange(newValue);
                                }}>
                                <CustomText text={option.name} size="xs" useTranslationText={false} />
                              </Pressable>
                            ))}
                          </ScrollView>
                        </View>
                      </View>
                    )}
                  />
                </View>

                <CustomSpacing size="s" type="vertical" />

                {/* By Specific Course */}
                <View>
                  <CustomText text="By Specific Course" size="xs" weight="bold" style={styles.targetingLabel} />
                  <Controller
                    control={control}
                    name="specificCourses"
                    render={({ field: { onChange, value } }) => (
                      <View style={styles.multiSelectContainer}>
                        <View style={styles.multiSelectBox}>
                          <ScrollView style={styles.multiSelectScroll} nestedScrollEnabled tabIndex={0}>
                            {courses.map((option) => (
                              <Pressable
                                key={option.id}
                                accessibilityRole="checkbox"
                                accessibilityState={{ checked: value?.includes(option.id) }}
                                style={({ pressed, hovered }: any) => [
                                  styles.multiSelectOption,
                                  value?.includes(option.id) && styles.multiSelectOptionSelected,
                                  hovered && styles.multiSelectOptionHovered,
                                  pressed && styles.multiSelectOptionPressed,
                                ]}
                                onPress={() => {
                                  const newValue = value?.includes(option.id)
                                    ? value.filter((v: string) => v !== option.id)
                                    : [...(value || []), option.id];
                                  onChange(newValue);
                                }}>
                                <CustomText text={option.name} size="xs" useTranslationText={false} />
                              </Pressable>
                            ))}
                          </ScrollView>
                        </View>
                        <CustomText
                          text="Selecting a degree program above will target all courses in that program"
                          size="xs"
                          style={styles.helpText}
                        />
                      </View>
                    )}
                  />
                </View>
              </View>
            )}

            {/* Publish Options */}
            <View style={styles.formGroup}>
              <CustomText
                text="Publish"
                size="s"
                weight={isFaculty ? "medium" : "ultraBold"}
                style={styles.formLabel}
              />
              <CustomSpacing size="s" type="vertical" />
              <View style={isFaculty ? { display: "flex", flexDirection: "row", gap: 12 } : styles.publishOptions}>
                {publishOptions.map((option) => (
                  <Controller
                    key={option.value}
                    control={control}
                    name="publishOption"
                    render={({ field: { onChange, value } }) => (
                      <Pressable
                        style={
                          isFaculty
                            ? []
                            : [styles.publishOption, value === option.value && styles.publishOptionSelected]
                        }
                        onPress={() => onChange(option.value)}>
                        <RadioButton
                          checked={value === option.value}
                          onPress={() => onChange(option.value)}
                          label={option.label}
                          size="small"
                        />
                      </Pressable>
                    )}
                  />
                ))}
              </View>
            </View>

            {/* Expiration Date */}
            {!isFaculty && (
              <View style={styles.formGroup}>
                <CustomText text="Expiration (Optional)" size="s" weight="ultraBold" style={styles.formLabel} />
                <CustomSpacing size="s" type="vertical" />
                <View style={styles.expirationContainer}>
                  <Controller
                    control={control}
                    name="expirationDate"
                    render={({ field: { value } }) => (
                      <DatePickerButton
                        date={value ? value.toLocaleDateString() : "Select date"}
                        onPress={handleDatePick}
                        buttonType="expiration"
                        testID="expiration-date-picker"
                        buttonStyle={styles.expirationButton}
                      />
                    )}
                  />
                  <CustomButton
                    text="Clear"
                    onPress={() => setValue("expirationDate", undefined)}
                    styleType="secondary"
                    style={styles.clearButton}
                    textStyle={styles.clearButtonText}
                  />
                </View>
                {errors?.expirationDate?.message && (
                  <CustomText size="xs" text={errors.expirationDate?.message} style={styles.formError} />
                )}
              </View>
            )}
          </View>
        </ScrollView>

        {/* Footer */}
        <View style={styles.footer}>
          <CustomButton
            text="Cancel"
            onPress={handleClose}
            styleType="secondary"
            style={styles.cancelButton}
            textStyle={styles.cancelButtonText}
          />
          <CustomButton
            text={getSubmitButtonText()}
            onPress={handleSubmit(handleFormSubmit)}
            styleType="primary"
            style={!isValid || isSubmitting ? { backgroundColor: "#ccc" } : styles.submitButton}
            textStyle={styles.submitButtonText}
            disabled={!isValid || isSubmitting}
          />
        </View>
      </CustomAdminModal>

      {/* Confirmation Modals */}
      <NewCustomAxioActionModal
        visible={modalState.state === AnnouncementModalState.ADD_CONFIRMATION}
        onClose={() => setModalState({ state: AnnouncementModalState.CLOSED })}
        modalTexts={modals.addConfirmation}
        contextualText={getValues("title")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === AnnouncementModalState.EDIT_CONFIRMATION}
        onClose={() => setModalState({ state: AnnouncementModalState.CLOSED })}
        modalTexts={modals.editConfirmation}
        contextualText={getValues("title")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === AnnouncementModalState.ADD_ERROR}
        onClose={() => setModalState({ state: AnnouncementModalState.CLOSED })}
        modalTexts={modals.addError}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === AnnouncementModalState.EDIT_ERROR}
        onClose={() => setModalState({ state: AnnouncementModalState.CLOSED })}
        modalTexts={modals.editError}
      />

      {/* Date Picker Modal */}
      <DatePickerModal
        locale="en"
        mode="single"
        label="Select expiration date"
        visible={isDatePickerVisible}
        onDismiss={handleDatePickerDismiss}
        onConfirm={handleDatePickerConfirm}
        date={getValues("expirationDate") || new Date()}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
  },
  container: {
    padding: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  formLabel: {
    color: "#182230",
  },
  formError: {
    color: "#EF4444",
  },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
  },
  helpText: {
    color: "#6B7280",
    marginTop: 4,
  },
  selectItem: {
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  selectedItem: {
    borderWidth: 1,
    backgroundColor: "#EEF4FF",
  },
  targetDescription: {
    color: "#6B7280",
    marginTop: 4,
  },
  targetAudienceItem: {
    padding: 16,
    width: "100%",
  },
  publishOptions: {
    gap: 12,
  },
  publishOption: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
  },
  publishOptionSelected: {
    backgroundColor: "#EEF4FF",
    borderColor: "#3E68FE",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  cancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  cancelButtonText: {
    color: "#6B7280",
  },
  submitButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: "#3E68FE",
  },
  submitButtonText: {
    color: "#FFFFFF",
  },
  expirationContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  clearButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
  },
  clearButtonText: {
    color: "#3E68FE",
    fontSize: 14,
  },
  expirationButton: {
    borderWidth: 1,
    borderColor: "#D0D5DD",
  },
  targetingSection: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  targetingSectionTitle: {
    color: "#182230",
  },
  targetingLabel: {
    color: "#374151",
    marginBottom: 8,
  },
  multiSelectContainer: {
    marginTop: 8,
  },
  multiSelectBox: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    maxHeight: 120,
  },
  courseSelectionBox: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
  },
  multiSelectCourseBox: {
    maxHeight: 300,
    padding: 16,
  },
  multiSelectScroll: {
    maxHeight: 120,
  },
  multiSelectOption: {
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  multiSelectOptionSelected: {
    backgroundColor: "#EEF4FF",
    borderBottomColor: "#B3CDFF",
  },
  multiSelectOptionHovered: {
    backgroundColor: "#F9FAFB",
  },
  multiSelectOptionPressed: {
    opacity: 0.7,
  },
  checkboxButton: {
    padding: 0,
    marginRight: 10,
  },
});

export default AnnouncementModal;
